import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Trash2, Pencil, Copy, ArrowUp, ArrowDown, BookOpen, Search,
} from 'lucide-react';
import {
  EmptyState, IconTile, Notice, PageHeader, Segmented, SoftCard, field, pill,
} from '@/components/PageKit';
import { Toggle } from '@/components/Toggle';
import {
  listenToCurricula, saveCurriculum, type Curriculum,
} from '@core/firebase/curricula';
import { validUnit, unitAyahs, type CurriculumUnit } from '@core/quran/curriculumProgress';
import { filterSurahs } from '@core/quran/surahSearch';
import { surahMeta } from '@core/quran/surahMeta';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';
import { applyFormat, inputAttrs } from '@core/format/inputs';

type Draft = {
  name: string;
  country: string;
  grade: string;
  term: string;
  units: CurriculumUnit[];
  published: boolean;
};

const EMPTY: Draft = { name: '', country: 'SA', grade: '1', term: '1', units: [], published: false };

/**
 * Where an admin defines what each grade must recite.
 *
 * Units are ADDED in teaching order and moved by hand — never sorted. The
 * order is the syllabus, and a term may open with a short surah from the end
 * of the mushaf.
 */
export default function AdminCurriculaPage() {
  const { t, language } = useLanguage();
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  /*
   * م-2-02: the source this draft was copied FROM, or null for an ordinary
   * edit. A curriculum's id is derived from (country, grade, term), so a
   * copy saved without changing one of them overwrites its own source —
   * silently, while the admin believes they made a second one.
   */
  const [copiedFrom, setCopiedFrom] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => listenToCurricula(setCurricula, () => setErrorKey('admin.curricula.failed')), []);

  const matches = useMemo(() => filterSurahs(term).slice(0, 20), [term]);
  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const surahName = (n: number) => {
    const meta = surahMeta(n);
    if (!meta) return String(n);
    return language === 'ar' ? meta.name : meta.englishName;
  };

  const patch = (change: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...change } : d));

  const addUnit = (surah: number) => {
    setDraft((d) => (d ? { ...d, units: [...d.units, { surah }] } : d));
    setTerm('');
  };

  const setRange = (i: number, from: string, to: string) => setDraft((d) => {
    if (!d) return d;
    const units = [...d.units];
    // Empty means the whole surah — the same "both ends or neither" the
    // validator and the server enforce.
    units[i] = from === '' && to === ''
      ? { surah: units[i].surah }
      : { surah: units[i].surah, from: Number(from), to: Number(to) };
    return { ...d, units };
  });

  const move = (i: number, by: number) => setDraft((d) => {
    if (!d) return d;
    const j = i + by;
    if (j < 0 || j >= d.units.length) return d;
    const units = [...d.units];
    [units[i], units[j]] = [units[j], units[i]];
    return { ...d, units };
  });

  const removeUnit = (i: number) => setDraft((d) => (
    d ? { ...d, units: d.units.filter((_, k) => k !== i) } : d
  ));

  const save = async () => {
    if (!draft || busy) return;
    if (!draft.name.trim()) { setErrorKey('admin.curricula.needName'); return; }
    if (draft.units.length === 0) { setErrorKey('admin.curricula.needUnits'); return; }
    // Named here as well as refused on the server, so the admin is told the
    // range is out of bounds rather than handed an internal error.
    if (!draft.units.every(validUnit)) { setErrorKey('admin.curricula.badRange'); return; }
    const targetId = `${draft.country.trim().toUpperCase()}-g${Number(draft.grade)}-t${Number(draft.term)}`;
    if (copiedFrom && curricula.some((c) => c.id === targetId)) {
      setErrorKey('admin.curricula.exists'); return;
    }
    setBusy(true);
    setErrorKey(null);
    try {
      await saveCurriculum({
        name: draft.name.trim(),
        country: draft.country.trim().toUpperCase(),
        grade: Number(draft.grade),
        term: Number(draft.term),
        units: draft.units,
        published: draft.published,
      });
      setDraft(null);
      setCopiedFrom(null);
    } catch {
      setErrorKey('admin.curricula.failed');
    } finally {
      setBusy(false);
    }
  };

  const small = 'w-20 rounded-full border border-border-strong/50 bg-card px-2 py-1.5 text-center text-sm text-ink outline-none focus:border-primary';
  const iconButton = 'flex h-9 w-9 items-center justify-center rounded-full text-helper transition-colors hover:bg-surface hover:text-ink';

  // Board a4: the list by country, when there is more than one.
  const countries = Array.from(new Set(curricula.map((c) => c.country))).sort();
  const shownCountry = country && countries.includes(country) ? country : null;
  const listed = shownCountry ? curricula.filter((c) => c.country === shownCountry) : curricula;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader
        title={t('admin.curricula.title')}
        subtitle={t('admin.curricula.subtitle')}
        actions={!draft ? (
          <button
            type="button"
            data-testid="new-curriculum"
            onClick={() => { setDraft({ ...EMPTY }); setErrorKey(null); }}
            className={cn(pill.base, pill.md, pill.primary)}
          >
            <Plus className="h-5 w-5" />
            {t('admin.curricula.new')}
          </button>
        ) : undefined}
      />

      {errorKey && <Notice tone="danger" testId="curricula-error">{t(errorKey)}</Notice>}

      {draft && (
        <SoftCard testId="curriculum-editor" className="space-y-5">
          {copiedFrom && (
            <p data-testid="copy-hint" className="rounded-[14px] bg-tint px-4 py-3 text-sm text-ink">
              {t('admin.curricula.copyHint')}
            </p>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <label className="block space-y-2">
                <span className="text-sm font-medium text-ink">{t('admin.curricula.name')}</span>
                <input
                  data-testid="curriculum-name"
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder={t('admin.curricula.namePlaceholder')}
                  className={field}
                />
              </label>

              <div className="grid grid-cols-3 gap-3">
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-ink">{t('admin.curricula.country')}</span>
                  <input
                    data-testid="curriculum-country"
                    value={draft.country}
                    onChange={(e) => patch({ country: e.target.value })}
                    maxLength={2}
                    dir="ltr"
                    className={field}
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-ink">{t('admin.curricula.grade')}</span>
                  <select
                    data-testid="curriculum-grade"
                    value={draft.grade}
                    onChange={(e) => patch({ grade: e.target.value })}
                    className={field}
                  >
                    {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </label>
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-ink">{t('admin.curricula.term')}</span>
                  <select
                    data-testid="curriculum-term"
                    value={draft.term}
                    onChange={(e) => patch({ term: e.target.value })}
                    className={field}
                  >
                    {['1', '2', '3'].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-[14px] bg-surface px-4 py-3">
                <span className="text-sm text-ink">{t('admin.curricula.publish')}</span>
                <Toggle
                  testId="curriculum-published"
                  checked={draft.published}
                  onChange={(next) => patch({ published: next })}
                  label={t('admin.curricula.publish')}
                />
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-ink">
                {t('admin.curricula.units')}
                {' · '}
                <span data-testid="unit-count" className="text-helper">{count(draft.units.length)}</span>
              </p>

              <ol data-testid="unit-list" className="divide-y divide-hair rounded-[18px] bg-surface/60 px-3">
                {draft.units.map((unit, i) => (
                  <li
                    key={`${unit.surah}-${i}`}
                    data-testid={`draft-unit-${i}`}
                    className="flex flex-wrap items-center gap-2 py-2"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-lime text-xs font-semibold text-accent-foreground">
                      {count(i + 1)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">
                      {surahName(unit.surah)}
                      <span className="ms-2 text-xs text-helper">{count(unitAyahs(unit))}</span>
                    </span>
                    <input
                      data-testid={`unit-from-${i}`}
                      value={unit.from ?? ''}
                      onChange={(e) => setRange(i, applyFormat('ayah', e.target.value), String(unit.to ?? ''))}
                      {...inputAttrs('ayah')}
                      placeholder={t('admin.curricula.from')}
                      inputMode="numeric"
                      dir="ltr"
                      className={small}
                    />
                    <input
                      data-testid={`unit-to-${i}`}
                      value={unit.to ?? ''}
                      onChange={(e) => setRange(i, String(unit.from ?? ''), applyFormat('ayah', e.target.value))}
                      {...inputAttrs('ayah')}
                      placeholder={t('admin.curricula.to')}
                      inputMode="numeric"
                      dir="ltr"
                      className={small}
                    />
                    {/* Moved by hand, never sorted: the order is the syllabus. */}
                    <button type="button" data-testid={`unit-up-${i}`} onClick={() => move(i, -1)} aria-label={t('admin.curricula.moveUp')} className={iconButton}>
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button type="button" data-testid={`unit-down-${i}`} onClick={() => move(i, 1)} aria-label={t('admin.curricula.moveDown')} className={iconButton}>
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button type="button" data-testid={`unit-remove-${i}`} onClick={() => removeUnit(i)} aria-label={t('admin.curricula.remove')} className={cn(iconButton, 'text-danger hover:bg-danger/10 hover:text-danger')}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ol>

              <label className="relative block">
                <span className="sr-only">{t('admin.curricula.addUnit')}</span>
                <Search className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-helper" />
                <input
                  data-testid="unit-search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t('admin.curricula.addUnit')}
                  className={cn(field, 'ps-12')}
                />
              </label>
              {term && (
                <ul className="max-h-56 space-y-1 overflow-y-auto rounded-[14px] bg-card p-1 ring-1 ring-hair">
                  {matches.map((meta) => (
                    <li key={meta.n}>
                      <button
                        type="button"
                        data-testid={`add-surah-${meta.n}`}
                        onClick={() => addUnit(meta.n)}
                        className="flex w-full items-center justify-between rounded-[10px] px-3 py-2 text-start text-sm text-ink hover:bg-tint"
                      >
                        <span className="truncate">{language === 'ar' ? meta.name : meta.englishName}</span>
                        <span className="shrink-0 text-helper">{count(meta.n)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-hair pt-4">
            <button
              type="button"
              data-testid="save-curriculum"
              onClick={() => { void save(); }}
              disabled={busy}
              aria-busy={busy}
              className={cn(pill.base, pill.md, pill.primary, 'min-w-[140px]')}
            >
              {t('admin.curricula.save')}
            </button>
            <button
              type="button"
              data-testid="cancel-curriculum"
              onClick={() => { setDraft(null); setErrorKey(null); }}
              className={cn(pill.base, pill.md, pill.quiet)}
            >
              {t('admin.curricula.cancel')}
            </button>
          </div>
        </SoftCard>
      )}

      {curricula.length === 0 && !draft ? (
        <SoftCard as="div">
          <EmptyState
            testId="no-curricula"
            icon={<BookOpen />}
            title={t('admin.curricula.emptyTitle')}
            body={t('admin.curricula.emptyBody')}
          />
        </SoftCard>
      ) : (
        <div className="space-y-4">
          {countries.length > 1 && (
            <Segmented
              label={t('admin.curricula.country')}
              value={shownCountry ?? ''}
              onChange={(v) => setCountry(v || null)}
              options={[
                { value: '', label: t('admin.users.filterAll'), testId: 'country-all' },
                ...countries.map((c) => ({ value: c, label: c, testId: `country-${c}` })),
              ]}
            />
          )}
          <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {listed.map((c) => (
              <li
                key={c.id}
                data-testid={`curriculum-row-${c.id}`}
                className="card-soft flex flex-col gap-4 p-5"
              >
                <div className="flex items-start gap-3">
                  <IconTile><BookOpen /></IconTile>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-ink">{c.name}</p>
                    <p className="text-sm text-helper">
                      {c.country} · {t('admin.curricula.grade')} {count(c.grade)}
                      {' · '}
                      {t('curriculum.term', { term: count(c.term) })}
                    </p>
                    <p className="text-sm text-helper">
                      {t('tracks.ayahCount', { count: c.totalAyahs, n: count(c.totalAyahs) })}
                    </p>
                  </div>
                  <span
                    data-testid={`state-${c.id}`}
                    className={cn(
                      'shrink-0 rounded-full px-3 py-1 text-xs font-semibold',
                      c.published ? 'bg-lime text-accent-foreground' : 'bg-surface text-helper',
                    )}
                  >
                    {t(c.published ? 'admin.curricula.published' : 'admin.curricula.draft')}
                  </span>
                </div>
                <div className="mt-auto flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid={`edit-${c.id}`}
                    onClick={() => {
                      setDraft({
                        name: c.name,
                        country: c.country,
                        grade: String(c.grade),
                        term: String(c.term),
                        units: c.units,
                        published: c.published,
                      });
                      setCopiedFrom(null);
                      setErrorKey(null);
                    }}
                    className={cn(pill.base, pill.sm, pill.lime)}
                  >
                    <Pencil className="h-4 w-4" />
                    {t('admin.curricula.edit')}
                  </button>
                  <button
                    type="button"
                    data-testid={`copy-${c.id}`}
                    onClick={() => {
                      // Unpublished: a copy is somebody's draft of next year's
                      // syllabus until they say otherwise.
                      setDraft({
                        name: c.name,
                        country: c.country,
                        grade: String(c.grade),
                        term: String(c.term),
                        units: c.units,
                        published: false,
                      });
                      setCopiedFrom(c.id);
                      setErrorKey(null);
                    }}
                    className={cn(pill.base, pill.sm, pill.quiet)}
                  >
                    <Copy className="h-4 w-4" />
                    {t('admin.curricula.copy')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
